import axios, { AxiosError, AxiosInstance } from "axios";

import get from "lodash/get";
import { FUNCTION_NAME, RESULT_CODE } from "constants/enum";
import env from "env";
import { BaseQueryArgs } from "./api.d";
import sessionUtil from "@/utils/sessionUtil";
import { clearAuth } from "@/slides/auth/authSlice";
import { showNotification } from "@/slides/notification/notificationSlice";
import i18n from "i18n/i18n";

const CONNECTION_TIMEOUT = 30000; // 30 seconds

const axiosInstance: AxiosInstance = axios.create({
  baseURL: env.REACT_APP_API_HOST,
  timeout: CONNECTION_TIMEOUT,
});

axiosInstance.interceptors.request.use(
  async (config) => {
    const functionCalled = get(config, "data.clientHeader.function", "");
    if (
      functionCalled === FUNCTION_NAME.GET_CAPTCHA ||
      functionCalled === FUNCTION_NAME.LOGIN
    ) {
      return config;
    }
    const newConfig = { ...config };

    const { store } = await import("@/stores");
    const state = store.getState();
    const { user } = state.auth;
    if (
      newConfig.data?.body?.header &&
      typeof newConfig.data.body.header === "object"
    ) {
      newConfig.data.body.header.sessionId = sessionUtil.getSession();
      newConfig.data.body.header.userId = user?.userId || "";
      newConfig.data.body.header.userName = "987978978";
      // D10 — lưới an toàn: đảm bảo body.header.language luôn theo ngôn ngữ đang bật.
      // (LOGIN/GET_CAPTCHA thoát sớm ở trên nên không qua đây — login xử lý riêng ở LoginWrapper.)
      newConfig.data.body.header.language = (i18n.language || "vi").toUpperCase();
    }
    return newConfig;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export const axiosBaseQuery =
  ({ baseUrl }: { baseUrl: string }) =>
  async ({
    url,
    method,
    data = {},
    params,
    headers,
    headerOnBody = {},
    functionName = "",
    categoryCode = "D_TRANSFER_EWALLET",
  }: BaseQueryArgs) => {
    const moment = Date.now().toString();
    const clientRequest = Math.floor(Math.random() * Date.now()).toString();

    try {
      const isFormData = data instanceof FormData;
      const timeout = get(data, "timeout", undefined) as number | undefined;
      // D10: gửi ngôn ngữ đang bật thay vì hardcode "VI". Backend đọc body.header.language
      // (328 chỗ header.getLanguage()); "VI"/"EN" — KHÔNG dùng "VN" (là mã quốc gia).
      const lang = (i18n.language || "vi").toUpperCase();

      const requestData = isFormData
        ? data
        : {
            clientHeader: {
              language: lang,
              clientRequestId: clientRequest,
              platform: "BIZ_WEB",
              service: "web",
              function: functionName,
              zonedClientTime: moment,
              clientAddress: "127.0.0.1",
              clientTime: new Date()
                .toISOString()
                .replace(/[-T:.Z]/g, "")
                .slice(0, 14),
              deviceId: "WEBLV24H",
            },
            body: {
              header: {
                appVersion: "v2.1",
                clientRequestId: clientRequest,
                zonedClientTime: moment,
                clientAddress: "127.0.0.1",
                clientTime: "20190925093222.939",
                language: lang,
                deviceId: "WEBLV24H",
                platform: "BIZ_WEB",
                channelCode: "WEB_LV24_KHDN",
                categoryCode,
                authorizedMode: 0,
                makerId: "LV24HWEB",
                checkerMode: 0,
                ip: "127.0.0.1",
                requestId: clientRequest,
                ...headerOnBody,
              },
              ...data,
            },
          };

      if (timeout !== undefined && !isFormData) {
        delete (requestData as any).body.timeout;
      }

      const response = await axiosInstance({
        // URL tuyệt đối (http/https) thì dùng nguyên văn, không ghép baseUrl —
        // cho phép trỏ RIÊNG một service về local mà không đổi host của cả app.
        url: url && /^https?:\/\//i.test(url) ? url : baseUrl + url,
        method,
        params,
        data: requestData,
        timeout,
      });

      const resultCode = get(response, "data.body.resultCode", "");
      const resultDesc = get(response, "data.body.resultDesc", "");
      const { store } = await import("@/stores");

      if (
        [
          RESULT_CODE.CAPTCHA_INFORMATION_NOT_MATCH_TRANSACTION,
          RESULT_CODE.CAPTCHA_SESSION_EXPIRED_ONE,
          RESULT_CODE.CAPTCHA_SESSION_EXPIRED_TWO,
          RESULT_CODE.CAPTCHA_SESSION_EXPIRED_THREE,
        ].includes(resultCode)
      ) {
        store.dispatch(clearAuth());
      }

      // Tài khoản bị khóa do nhập sai OTP quá số lần cho phép → buộc đăng xuất ngay.
      // Bỏ qua màn login (login tự hiển thị thông báo qua resultDesc của chính nó).
      if (
        resultCode === RESULT_CODE.OTP_CLOSE_MODAL &&
        functionName !== FUNCTION_NAME.LOGIN
      ) {
        store.dispatch(
          showNotification({
            title: i18n.t("NOTICE"),
            message:
              resultDesc ||
              i18n.t("TAI_KHOAN_CUA_BAN_A_BI_KHOA_DO_NHAP_OTP_"),
            type: "error",
          }),
        );
        store.dispatch(clearAuth());
      }
      return { data: response.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;

      // if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
      //   const { store } = await import("@/stores");
      //   store.dispatch(
      //     showNotification({
      //       title: i18n.t("NOTICE"),
      //       message:
      //         i18n.t("KET_NOI_EN_MAY_CHU_QUA_THOI_GIAN_CHO_PHE"),
      //       type: "error",
      //     }),
      //   );
      // }

      return {
        error: {
          status: err.response?.status || 500,
          data:
            get(err, "response.data.userMessage") ||
            get(err, "response.data.message") ||
            get(err, "response.data.error") ||
            "Unknown error",
        },
      };
    }
  };
